import API from './api.config';

/**
 * Base64 URL to Uint8Array helper for VAPID applicationServerKey
 */
export function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const NotificationService = {
  // ─── USER & SUBSCRIBER METHODS ──────────────────────────────────────────────

  /**
   * Get public VAPID key from backend
   */
  getVapidPublicKey: async () => {
    const res = await API.get('/notifications/vapid-key');
    return res.data?.publicKey;
  },

  /**
   * Register push device token (Web push subscription or mobile token)
   */
  registerDeviceToken: async (payload) => {
    const res = await API.post('/notifications/register-token', payload);
    return res.data;
  },

  /**
   * Unregister push token
   */
  unregisterDeviceToken: async (token) => {
    const res = await API.delete('/notifications/unregister-token', { data: { token } });
    return res.data;
  },

  /**
   * Fetch in-app notifications
   */
  getNotifications: async ({ page = 1, limit = 20, unreadOnly = false } = {}) => {
    const res = await API.get('/notifications', {
      params: { page, limit, unreadOnly }
    });
    return res.data;
  },

  /**
   * Fetch fast unread count for navbar badge
   */
  getUnreadCount: async () => {
    const res = await API.get('/notifications/unread-count');
    return res.data?.unreadCount || 0;
  },

  /**
   * Mark a single notification as read
   */
  markAsRead: async (notificationId) => {
    const res = await API.patch(`/notifications/${notificationId}/read`);
    return res.data;
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async () => {
    const res = await API.patch('/notifications/read-all');
    return res.data;
  },

  /**
   * Delete a notification
   */
  deleteNotification: async (notificationId) => {
    const res = await API.delete(`/notifications/${notificationId}`);
    return res.data;
  },

  // ─── WEB BROWSER PUSH HELPERS ───────────────────────────────────────────────

  /**
   * Check if web browser supports service worker and push notifications
   */
  isWebPushSupported: () => {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  },

  /**
   * Get current browser notification permission ('default' | 'granted' | 'denied')
   */
  getPermissionState: () => {
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission;
  },

  /**
   * Register Service Worker and subscribe to Web Push
   */
  subscribeToWebPush: async () => {
    if (!NotificationService.isWebPushSupported()) {
      throw new Error('Web Push is not supported in this browser');
    }

    // 1. Request native permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { success: false, permission };
    }

    // 2. Register Service Worker
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    await navigator.serviceWorker.ready;

    // 3. Fetch VAPID key
    const publicKey = await NotificationService.getVapidPublicKey();
    if (!publicKey) {
      throw new Error('VAPID public key not available from server');
    }

    // 4. Subscribe to Push Manager
    const convertedVapidKey = urlBase64ToUint8Array(publicKey);
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey
      });
    }

    // 5. Send subscription to KnightSteps backend
    const subJson = subscription.toJSON();
    const token = subJson.endpoint;
    const p256dh = subJson.keys?.p256dh;
    const auth = subJson.keys?.auth;

    await NotificationService.registerDeviceToken({
      platform: 'web',
      token,
      endpoint: subJson.endpoint,
      p256dh,
      auth,
      deviceModel: navigator.userAgent.substring(0, 80),
      appVersion: '1.0.0'
    });

    return { success: true, permission, subscription };
  },

  // ─── ADMIN BROADCAST METHODS (ADMIN ONLY) ───────────────────────────────────

  /**
   * Admin broadcast to target audience
   */
  broadcastNotification: async (broadcastPayload) => {
    const res = await API.post('/admin/notifications/broadcast', broadcastPayload);
    return res.data;
  },

  /**
   * Test push notification to admin device
   */
  testSendNotification: async (testPayload) => {
    const res = await API.post('/admin/notifications/test-send', testPayload);
    return res.data;
  },

  /**
   * Admin broadcast history
   */
  getBroadcastHistory: async ({ page = 1, limit = 20 } = {}) => {
    const res = await API.get('/admin/notifications/history', {
      params: { page, limit }
    });
    return res.data;
  },

  /**
   * Admin subscriber statistics
   */
  getSubscriberStats: async () => {
    const res = await API.get('/admin/notifications/stats');
    return res.data?.stats;
  }
};

export default NotificationService;
